import { useCallback, useRef, useState } from "react";
import type React from "react";
import {
  getCachedMutationProcessingSettings,
  setCachedMutationProcessingSettings,
  updateCachedMutationProcessingSetting,
  updateMutationProcessingSetting,
} from "../api/mutationProcessing.api";
import type {
  MutationProcessingResponse,
  MutationProcessingSetting,
  MutationProcessingUpdatePayload,
} from "../types/mutationProcessing.types";

interface UseUpdateMutationProcessingSettingOptions {
  onSuccess?: (setting: MutationProcessingSetting) => void;
  onError?: (error: unknown) => void;
}

export const useUpdateMutationProcessingSetting = (
  setSettings: React.Dispatch<React.SetStateAction<MutationProcessingResponse>>,
  options: UseUpdateMutationProcessingSettingOptions = {}
) => {
  const [savingType, setSavingType] = useState<string | null>(null);
  const savingTypeRef = useRef<string | null>(null);

  const updateSetting = useCallback(
    async (setting: MutationProcessingSetting, payload: MutationProcessingUpdatePayload) => {
      if (savingTypeRef.current) return;

      const previousSettings = getCachedMutationProcessingSettings();
      const optimisticSetting = { ...setting, ...payload };
      savingTypeRef.current = setting.mutationType;
      setSavingType(setting.mutationType);
      setSettings((current) =>
        current.map((item) => (item.mutationType === setting.mutationType ? optimisticSetting : item))
      );
      updateCachedMutationProcessingSetting(setting.mutationType, () => optimisticSetting);

      try {
        const updated = await updateMutationProcessingSetting(setting.mutationType, payload);
        setSettings((current) =>
          current.map((item) => (item.mutationType === updated.mutationType ? updated : item))
        );
        options.onSuccess?.(updated);
      } catch (updateError) {
        if (previousSettings) {
          setCachedMutationProcessingSettings(previousSettings);
          setSettings(previousSettings);
        }
        options.onError?.(updateError);
      } finally {
        savingTypeRef.current = null;
        setSavingType(null);
      }
    },
    [options, setSettings]
  );

  return {
    savingType,
    updateSetting,
  };
};
